import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Eyebrow } from './Eyebrow';
import { TextField } from './form/TextField';

export interface EmailGatePayload {
  email: string;
  consent: boolean;
}

export interface EmailGateProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: EmailGatePayload) => void | Promise<void>;
  submitting?: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailGate({ open, onClose, onSubmit, submitting = false }: EmailGateProps) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [consentError, setConsentError] = useState<string | undefined>();
  const emailRef = useRef<HTMLInputElement | null>(null);

  // Auto-focus email when modal opens.
  useEffect(() => {
    if (!open) return;
    // Defer so Modal's own focus-trap runs first; our input trumps.
    const t = setTimeout(() => {
      emailRef.current?.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [open]);

  // Reset fields each time the modal is re-opened.
  useEffect(() => {
    if (!open) {
      setEmail('');
      setConsent(false);
      setEmailError(undefined);
      setConsentError(undefined);
    }
  }, [open]);

  const handle = async (e: FormEvent) => {
    e.preventDefault();
    let bad = false;
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError('Enter a valid email address.');
      bad = true;
    } else {
      setEmailError(undefined);
    }
    if (!consent) {
      setConsentError('Please confirm it’s OK to contact you.');
      bad = true;
    } else {
      setConsentError(undefined);
    }
    if (bad) return;
    await onSubmit({ email: email.trim(), consent });
  };

  return (
    <Modal open={open} onClose={onClose} title="One more step">
      <Eyebrow className="-mt-2 mb-3">Almost there</Eyebrow>
      <p className="text-[0.9375rem] leading-relaxed text-[color:var(--color-ink)]/80">
        Your indication is ready. Enter your email and a Zachary broker will follow up with the
        formal indication. Usually within two business days. We don&apos;t share your information.
      </p>
      <form onSubmit={handle} className="mt-6 flex flex-col gap-5" noValidate>
        <TextField
          ref={emailRef}
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailError}
        />
        <label className="flex items-start gap-3 text-[0.875rem] leading-relaxed cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[color:var(--color-blue)]"
            aria-invalid={consentError ? true : undefined}
          />
          <span>It&apos;s OK to contact me about this project.</span>
        </label>
        {consentError && (
          <p className="-mt-3 text-[0.75rem] text-red-600" role="alert">
            {consentError}
          </p>
        )}
        <div className="mt-2 flex flex-col gap-3">
          <Button
            variant="secondary"
            type="submit"
            disabled={submitting}
            className="w-full"
          >
            {submitting ? 'Calculating…' : 'Reveal my indication'}
          </Button>
          <Button variant="ghost" onClick={onClose} type="button" className="self-center">
            Not yet. Go back
          </Button>
        </div>
      </form>
    </Modal>
  );
}
